/**
 * useSystemHealth — Hook für das System-Status-Dashboard
 *
 * Ruft /api/system-health auf und refresht alle 60 Sekunden.
 * Anti-Flicker: useRef für stabile Referenz, einmaliges Laden.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export type HealthStatus = 'ok' | 'warnung' | 'fehler';

export interface ServiceHealth {
  status: HealthStatus;
  label: string;
  details?: string;
  letzterSync?: string | null;
}

export interface AgentenHealth {
  status: HealthStatus;
  label: string;
  aktiveAgenten: number;
  letzterDurchlauf: string | null;
  offeneVorschlaege: number;
}

export interface SingleDeploymentHealth {
  status: HealthStatus;
  label: string;
  letztes?: { url?: string; state?: string; created?: string } | null;
}

export interface DeploymentHealth {
  production: SingleDeploymentHealth;
  preview: SingleDeploymentHealth;
}

export interface WinvalueRechnungenReport {
  akten_mit_winvalue_uid: number;
  akten_synchronisiert: number;
  akten_ausstehend: number;
  rechnungen_gesamt: number;
  letzter_sync: string | null;
  runs_letzte_24h: number;
  fehler_letzte_24h: number;
  neu_letzte_24h: number;
  geaendert_letzte_24h: number;
}

export interface SystemHealthData {
  timestamp: string;
  supabase: ServiceHealth;
  github: ServiceHealth;
  gmail: ServiceHealth;
  winvalue: ServiceHealth;
  winvalueRechnungen: ServiceHealth;
  winvalueRechnungenReport: WinvalueRechnungenReport | null;
  email: ServiceHealth;
  agenten: AgentenHealth;
  deployments: DeploymentHealth;
}

const REFRESH_INTERVAL = 60_000; // 60 Sekunden

export function useSystemHealth() {
  const [health, setHealth] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const hasLoadedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/system-health');
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('API nicht erreichbar — bitte API-Dev-Server starten (port 3001)');
      }
      const data: SystemHealthData = await res.json();
      setHealth(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchHealth();
    }

    intervalRef.current = setInterval(() => fetchHealth(true), REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchHealth]);

  const refresh = useCallback(() => fetchHealth(false), [fetchHealth]);

  return { health, loading, error, lastRefresh, refresh };
}
